import { useEffect, useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import type { RiskControlKey } from "~/v3-poc/api/riskControls";
import {
  getRiskControlChange,
  getRiskControlsDialogMode,
  type RiskControlChange,
  type RiskControlSelection,
  type RiskControlsDialogMode,
  type RiskControlsDialogView,
  type RiskControlsFormValues,
  riskControlsFormSchema,
} from "./riskControls.model";

export interface UseRiskControlsDialogControllerArgs {
  /** Server state. A primitive, so no memoisation is needed at the call site. */
  current: RiskControlSelection;
  open: boolean;
  onApply: (change: RiskControlChange) => void;
  /** Clears a stale liquidate-all result when the view is left or re-entered. */
  onViewChange?: (view: RiskControlsDialogView) => void;
}

export interface RiskControlsDialogController {
  mode: RiskControlsDialogMode;
  view: RiskControlsDialogView;
  /** The control to lift. Only set in `lift` mode. */
  activeControl: RiskControlKey | null;
  selection: RiskControlSelection;
  change: RiskControlChange | null;
  canApply: boolean;
  /** Checking one control selects it; unchecking returns to no restriction. */
  setControlChecked: (control: RiskControlKey, checked: boolean) => void;
  setView: (view: RiskControlsDialogView) => void;
  /** `select` mode — submitting the chosen restriction. */
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  /** `lift` mode — the single available action, so it needs no form. */
  onConfirmLift: () => void;
}

export const useRiskControlsDialogController = ({
  current,
  open,
  onApply,
  onViewChange,
}: UseRiskControlsDialogControllerArgs): RiskControlsDialogController => {
  const { handleSubmit, reset, setValue, watch } =
    useForm<RiskControlsFormValues>({
      resolver: zodResolver(riskControlsFormSchema),
      defaultValues: { selection: current },
    });

  const [view, setViewState] = useState<RiskControlsDialogView>("controls");

  // biome-ignore lint/correctness/useExhaustiveDependencies: `open` is a trigger — reopening the dialog discards an abandoned edit and re-seeds from server state. The body writes rather than reads it.
  useEffect(() => {
    reset({ selection: current });
    setViewState("controls");
  }, [open, current, reset]);

  const selection = watch("selection");
  const change = useMemo(
    () => getRiskControlChange(current, selection),
    [current, selection],
  );

  const setView = (next: RiskControlsDialogView) => {
    setViewState(next);
    onViewChange?.(next);
  };

  return {
    mode: getRiskControlsDialogMode(current),
    view,
    activeControl: current,
    selection,
    change,
    canApply: change !== null,
    setControlChecked: (control, checked) =>
      setValue("selection", checked ? control : null),
    setView,
    onSubmit: handleSubmit((submitted) => {
      const applied = getRiskControlChange(current, submitted.selection);
      if (applied) {
        onApply(applied);
      }
    }),
    onConfirmLift: () => {
      const lifted = getRiskControlChange(current, null);
      if (lifted) {
        onApply(lifted);
      }
    },
  };
};
